import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Plus, Edit, Trash2, User, Shield, UserCheck, Eye, EyeOff, Factory } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminUsersPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    full_name: "",
    salesperson_id: "",
    email: "",
    role: "employee",
    is_active: true
  });

  useEffect(() => {
    const adminAuth = sessionStorage.getItem("adminAuth");
    const storedUser = sessionStorage.getItem("adminUser");
    
    if (adminAuth !== "true") {
      navigate("/admin");
      return;
    }
    
    if (storedUser) {
      const user = JSON.parse(storedUser);
      setCurrentUser(user);
      
      // Only admins can access this page
      if (user.role !== "admin") {
        toast.error("Access denied. Admin only.");
        navigate("/admin");
        return;
      }
    }
    
    fetchUsers();
  }, [navigate]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/auth/users`);
      setUsers(response.data);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.username || !formData.full_name || !formData.salesperson_id) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    if (!editingUser && !formData.password) {
      toast.error("Password is required for new users");
      return;
    }

    try {
      if (editingUser) {
        // Update existing user
        await axios.put(`${API}/admin/auth/users/${editingUser.id}`, {
          full_name: formData.full_name,
          salesperson_id: formData.salesperson_id,
          email: formData.email,
          role: formData.role,
          is_active: formData.is_active
        });
        toast.success("User updated successfully");
      } else {
        // Create new user
        await axios.post(`${API}/admin/auth/users`, formData);
        toast.success("User created successfully");
      }
      
      setShowForm(false);
      setEditingUser(null);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save user");
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      password: "",
      full_name: user.full_name,
      salesperson_id: user.salesperson_id,
      email: user.email || "",
      role: user.role,
      is_active: user.is_active
    });
    setShowForm(true);
  };

  const handleDelete = async (user) => {
    if (user.id === currentUser?.id) {
      toast.error("You cannot delete your own account");
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete ${user.full_name}?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/auth/users/${user.id}`);
      toast.success("User deleted successfully");
      fetchUsers();
    } catch (error) {
      toast.error("Failed to delete user");
    }
  };

  const resetForm = () => {
    setFormData({
      username: "",
      password: "",
      full_name: "",
      salesperson_id: "",
      email: "",
      role: "employee",
      is_active: true
    });
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case "admin": return <Shield size={14} className="text-red-400" />;
      case "manager": return <UserCheck size={14} className="text-yellow-400" />;
      case "factory": return <Factory size={14} className="text-orange-400" />;
      default: return <User size={14} className="text-blue-400" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-12">
      {/* Header */}
      <section className="py-6 border-b border-white/5">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <Button variant="ghost" onClick={() => navigate("/admin")} className="mb-4">
            <ArrowLeft size={16} className="mr-2" /> Back to Dashboard
          </Button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-serif text-2xl">User Management</h1>
              <p className="text-sm text-muted-foreground">Manage admin users and employee access</p>
            </div>
            <Button onClick={() => { resetForm(); setEditingUser(null); setShowForm(true); }}>
              <Plus size={16} className="mr-2" /> Add User
            </Button>
          </div>
        </div>
      </section>

      {/* Users List */}
      <section className="py-8">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="grid gap-4">
            {users.map((user) => (
              <motion.div
                key={user.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`bg-card border border-white/10 p-4 rounded-lg ${!user.is_active ? 'opacity-50' : ''}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      {getRoleIcon(user.role)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium">{user.full_name}</h3>
                        {!user.is_active && (
                          <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded">Inactive</span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">@{user.username}</p>
                      <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                        <span>Salesperson ID: <span className="font-mono text-primary">{user.salesperson_id}</span></span>
                        <span className={`px-2 py-0.5 rounded ${
                          user.role === 'admin' ? 'bg-red-500/20 text-red-400' :
                          user.role === 'manager' ? 'bg-yellow-500/20 text-yellow-400' :
                          user.role === 'factory' ? 'bg-orange-500/20 text-orange-400' :
                          'bg-blue-500/20 text-blue-400'
                        }`}>
                          {user.role}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleEdit(user)}>
                      <Edit size={14} className="mr-1" /> Edit
                    </Button>
                    {user.id !== currentUser?.id && (
                      <Button variant="outline" size="sm" onClick={() => handleDelete(user)} className="text-red-400 hover:bg-red-500/10">
                        <Trash2 size={14} />
                      </Button>
                    )}
                  </div>
                </div>
                
                {user.last_login && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Last login: {new Date(user.last_login).toLocaleString()}
                  </p>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* User Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingUser ? "Edit User" : "Add New User"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Username *</label>
              <Input
                value={formData.username}
                onChange={(e) => setFormData(p => ({ ...p, username: e.target.value }))}
                className="bg-background border-white/10"
                placeholder="Enter username"
                disabled={!!editingUser}
              />
            </div>
            
            {!editingUser && (
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Password *</label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData(p => ({ ...p, password: e.target.value }))}
                    className="bg-background border-white/10 pr-10"
                    placeholder="Enter password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
            )}
            
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Full Name *</label>
              <Input
                value={formData.full_name}
                onChange={(e) => setFormData(p => ({ ...p, full_name: e.target.value }))}
                className="bg-background border-white/10"
                placeholder="Enter full name"
              />
            </div>
            
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Salesperson ID *</label>
              <Input
                value={formData.salesperson_id}
                onChange={(e) => setFormData(p => ({ ...p, salesperson_id: e.target.value.toUpperCase() }))}
                className="bg-background border-white/10 font-mono"
                placeholder="e.g., JD001"
              />
              <p className="text-xs text-muted-foreground mt-1">Unique ID used for tracking orders</p>
            </div>
            
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Email</label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))}
                className="bg-background border-white/10"
                placeholder="Enter email"
              />
            </div>
            
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Role *</label>
              <Select value={formData.role} onValueChange={(v) => setFormData(p => ({ ...p, role: v }))}>
                <SelectTrigger className="bg-background border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="employee">Employee</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="factory">Factory</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Factory role is for production team access
              </p>
            </div>
            
            {editingUser && (
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={formData.is_active}
                  onCheckedChange={(c) => setFormData(p => ({ ...p, is_active: c }))}
                />
                <span className="text-sm">Active Account</span>
              </label>
            )}
            
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
              <Button type="submit" className="flex-1">
                {editingUser ? "Update User" : "Create User"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsersPage;
